<template>

  <v-container fluid class="page page-receiver-order-list">

    <!---------------->
    <!-- Breadcrumb -->
    <!---------------->
    <v-row class="mt-3 mb-6">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <v-icon small class="mr-2">mdi-package-variant-closed</v-icon>
        <div>Orders</div>
      </v-col>
    </v-row>

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="page-content">
      <v-row no-gutters>
        <v-col cols="12" xs="12" sm="12" md="4" lg="4">
          <span class="mr-3 page-label">Orders</span>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="info-timezone" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <div>Dates & times are: {{ activeTimezone }}</div>
          </v-tooltip>
        </v-col>
        <!-- Filter options -->
        <v-col cols="12" xs="12" sm="12" md="8" lg="8" align="right" class="filter-options-container">
          <order-status-selector
              class="order-status-selector"
              :statuses="availableStatuses"
              v-on:changed="updateSelectedStatusFilter" />
        </v-col>
      </v-row>
    </v-container>

    <!---------------->
    <!-- Order List -->
    <!---------------->
    <v-container fluid class="mt-4 page-content">
      <v-row no-gutters align="center" justify="start">
        <!-- Find by order number -->
        <v-col cols="12" xs="12" sm="6" md="4" lg="3">
          <v-text-field
              class="pl-2 pt-0 order-number-finder"
              clearable
              single-line
              hide-details
              label="Order number"
              append-icon="mdi-magnify"
              v-model="search" />
        </v-col>
        <!-- Bulk action -->
        <v-col cols="12" xs="12" sm="6" md="8" lg="9" align="right">
          <template v-if="selected.length > 0">
            <span class="mr-4" style="color:#9c9c9c;" v-if="selectedItemCount > 0">{{ selectedItemCount }} Selected</span>
            <v-btn color="blue" width="180" @click="handleDownloadMultipleInvoices">
              <span v-if="selectedItemCount === 1">Download Invoice</span>
              <span v-else>Download Invoices</span>
            </v-btn>
          </template>
        </v-col>
      </v-row>

      <v-data-table
          class="mt-8"
          :headers="headers"
          :items="orderItems"
          :search="search"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems"
          :show-select="true"
          v-model="selected">

        <!-- routeleaf order number (link to order info page) -->
        <template v-slot:[`item.order_number`]="{ item }">
          <!-- (auto-placed order due to previous delivery rejection or rescheduling) -->
          <template v-if="item.root_order_id">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="orange accent-3"
                    style="cursor:pointer;font-size:24px;margin-right:3px;padding-bottom:5px;"
                    v-bind="attrs"
                    v-on="on">
                  mdi-repeat-variant
                </v-icon>
              </template>
              <div style="font-weight:600">(Re-submitted order)</div>
              <!-- due to rejection -->
              <template v-if="item.parent_order && item.parent_order.rejected_at">
                <div>Last delivery was rejected by receiving agent:</div>
                <div v-if="item.parent_order.receiver_notes" class="mt-1">{{ item.parent_order.receiver_notes }}</div>
              </template>
              <!-- due to rescheduling -->
              <template v-if="item.parent_order && item.parent_order.rescheduled_at">
                <div>Last delivery was rescheduled by dispatcher:</div>
                <div v-if="item.parent_order.dispatcher_notes && item.parent_order.dispatcher_notes.reschedule" class="mt-1">
                  {{ item.parent_order.dispatcher_notes.reschedule }}
                </div>
              </template>
            </v-tooltip>
          </template>
          <span style="font-size:1.2em">
            <router-link :to="generateOrderInfoPageLink(item)">
              {{ item.thirdparty_order_number }}
            </router-link>
          </span>
        </template>

        <!-- order total -->
        <template v-slot:item.order_details.total="{ item }">
          <span class="order-total">{{ formatTotal(item.order_details) }}</span>
        </template>

        <!-- provider alias -->
        <template v-slot:item.provider_alias="{ item }">
          <span class="provider-name">{{ formatProvider(item.provider_alias) }}</span>
        </template>

        <!-- provider brand -->
        <template v-slot:[`item.thirdparty_brand_name`]="{ item }">
          <span>{{ item.thirdparty_brand_name }}</span>
        </template>

        <!-- pickup location (shipper) -->
        <template v-slot:[`item.shipper.name`]="{ item }">
          {{ formatLocation(item.shipper) }}
        </template>

        <!-- dropoff location (receiver) -->
        <template v-slot:[`item.receiver.name`]="{ item }">
          <!-- (note on approval requirements) -->
          <v-tooltip top v-if="item.receiver.receiver_status === 'PENDING_APPROVAL'">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="yellow darken-3"
                  style="cursor:pointer;font-size:21px;margin-right:3px;"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>Order cannot be scheduled until Receiver is approved.</div>
          </v-tooltip>
          <router-link :to="generateLocationPageLink(item.receiver)">
            {{ formatLocation(item.receiver) }}
          </router-link>
        </template>

        <!-- scheduled delivery date -->
        <template v-slot:[`item.scheduled_delivery`]="{ item }">
          <span>{{ formatDate(item.scheduled_delivery) }}</span>
        </template>

        <!-- attachment docs -->
        <template v-slot:item.attachment_docs="{ item }">
          <span>{{ Object.keys(item.attachment_docs).length }}</span>
        </template>

        <!-- order status -->
        <template v-slot:item.order_status="{ item }">
          <v-chip label outlined :class="item.order_status">{{ item.order_status }}</v-chip>
        </template>

        <!-- (download menu) -->
        <template v-slot:[`item.id`]="{ item }">
          <v-menu offset-y v-if="$root.isOrderInvoiceGuaranteed(item) || Object.keys(item.attachment_docs).length > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="white"
                  v-bind="attrs"
                  v-on="on">
                mdi-dots-horizontal
              </v-icon>
            </template>
            <v-list>
              <v-list-item class="px-2 menu-item" v-if="$root.isOrderInvoiceGuaranteed(item)">
                <v-btn plain @click="downloadOrderInvoice(item)">Order Invoice</v-btn>
              </v-list-item>
              <v-list-item class="px-2 menu-item" v-if="Object.keys(item.attachment_docs).length > 0">
                <v-btn plain @click="downloadOrderAttachments(item)">Attachment Docs</v-btn>
              </v-list-item>
            </v-list>
          </v-menu>
          <span style="color:#6c6c6c" v-else>n/a</span>
        </template>
      </v-data-table>
    </v-container>

  </v-container>

</template>

<script>
import { mapState } from 'vuex'
import OrderStatusSelector from '@/components/orders/OrderStatusSelector'

export default {
  name: 'ReceiverOrderListPage',

  components: {
    OrderStatusSelector
  },

  data () {
    return {
      headers: [
        { text: 'Order #', value: 'order_number' },
        // { text: 'Provider', value: 'provider_alias' },
        { text: 'Brand', width: '120px', value: 'thirdparty_brand_name' },
        { text: 'Total', value: 'order_details.total' },
        { text: 'From', value: 'shipper.name' },
        { text: 'To', value: 'receiver.name' },
        { text: 'Delivery Date', align: 'center', value: 'scheduled_delivery' },
        { text: 'Attachment Docs', align: 'center', value: 'attachment_docs' },
        { text: 'Status', value: 'order_status' },
        { text: 'Download', sortable: false, align: 'center', value: 'id', width: '5%' }
      ],
      isDataLoading: false,
      dataLoadingError: null,
      orderData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 25,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['placed_at'],
        sortDesc: [true]
      },
      search: null,
      selected: [],
      // ------------------------ Filter handling
      availableStatuses: [
        { label: 'placed', code: 'PLACED' },
        { label: 'being-scheduled', code: 'BEING_SCHEDULED' },
        { label: 'scheduled', code: 'SCHEDULED' },
        { label: 'to-shipper', code: 'TO_SHIPPER' },
        { label: 'at-shipper', code: 'AT_SHIPPER' },
        { label: 'picked-up', code: 'PICKED_UP' },
        { label: 'to-receiver', code: 'TO_RECEIVER' },
        { label: 'at-receiver', code: 'AT_RECEIVER' },
        { label: 'delivered', code: 'DELIVERED' },
        { label: 'canceled', code: 'CANCELED' }
      ],
      statusFilter: []
    }
  },

  computed: {
    activeTimezone () {
      return this.$store.getters['app/defaultTimezone']
    },
    orderItems () {
      return (this.orderData) ? this.orderData.items : []
    },
    dataMeta () {
      return (this.orderData) ? this.orderData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    },
    selectedItemCount () {
      return (this.selected) ? this.selected.length : 0
    },
    ...mapState('app', {
      downloadStatus: state => state.downloadStatus
    })
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    },
    // Observe changes to the status filter
    statusFilter: function (updated) {
      this.queryData()
    },
    // Observe changes to the search field
    search: function (updated) {
      this.queryData()
    }
  },

  methods: {
    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MMMM D, YYYY') : '-'
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'MMM D, YYYY h:mma') : ''
    },

    // Display total
    formatTotal (orderDetails = {}) {
      const totalAsNum = (orderDetails.total) ? Number(orderDetails.total) : 0
      return `$${totalAsNum.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },

    // Display provider name
    formatProvider (alias) {
      return (alias) ? this.$root.providerAliasToName(alias) : ''
    },

    // Display location name
    formatLocation (loc) {
      return (loc && loc.name) ? loc.name : '-'
    },

    // Generate link data for shipper/receiver page (for a provided location item)
    generateLocationPageLink (loc) {
      if (loc && loc.model) {
        if (loc.model === 'Shipper') {
          return {
            name: 'shipper-item',
            params: { shipperId: loc.id }
          }
        } else if (loc.model === 'Receiver') {
          return {
            name: 'receiver-item',
            params: { receiverId: loc.id }
          }
        } else {
          return ''
        }
      } else {
        return ''
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (item) {
      if (item && item.model) {
        return {
          name: 'receiver-order-info-summary',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    // Handle bulk order invoice download action
    handleDownloadMultipleInvoices () {
      const validOrders = this.selected.filter(order => this.$root.isOrderInvoiceGuaranteed(order))
      this.$root.sendDownloadOrderInvoiceRequest(validOrders)
      this.selected = []
    },

    // Send request to download order invoice
    downloadOrderInvoice (order) {
      this.$root.sendDownloadOrderInvoiceRequest([order])
    },

    // Send request to download order attachments
    downloadOrderAttachments (order) {
      this.$root.sendDownloadOrderAttachmentsRequest(order)
    },

    // Update status filters
    updateSelectedStatusFilter (selected) {
      this.statusFilter = selected
    },

    // Perform query of placed orders
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          with: 'shipper,receiver,order_details,parent_order'
        }

        // Apply status filters
        if (this.statusFilter.length > 0) params.status = this.statusFilter

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply search query (for order_number only)
        if (this.search) params['order_number.contains'] = this.search

        // Retrieve results
        this.orderData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'receiver-orders',
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .page-receiver-order-list .info-timezone {
    font-size: 1.25em;
    color: #6a6a6a;
    padding-bottom: 12px;
    cursor:pointer;
  }

  .page-receiver-order-list .filter-options-container {
    /* border: 1px solid #525252;
    border-radius: 4px; */
  }

  .page-receiver-order-list .order-status-selector {
    max-width: 340px;
  }

  .page-receiver-order-list .menu-item:hover {
    background-color: #525252;
  }

  .page-receiver-order-list .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .page-receiver-order-list .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .page-receiver-order-list .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 120px;
  }

  .page-receiver-order-list .v-data-table a {
    text-decoration: none;
  }
  .page-receiver-order-list .v-data-table a:hover {
    text-decoration: underline;
  }

  @media screen and (max-width: 940px) {
    .page-receiver-order-list .order-status-selector {
      margin-top: 22px;
      max-width: none;
    }

    .page-receiver-order-list .order-number-finder {
      margin-top: 14px;
    }
  }
</style>
